<script setup>
import { computed, inject, ref, onMounted } from 'vue';
import { useFaqStore } from '@/app/stores/faqStore.js';
import { useInterpolatedContent } from '@/app/composables/useInterpolatedContent.js';
import BaseAccordion from '@/app/components/shared/base-accordion.vue';

const config = inject('config');
const gaPush = inject('gaPush');
const faqStore = useFaqStore();

const faqAccordionHeadingRef = ref(null);
const showAllAccordions = ref(false);

const accordions = computed(() => {
  if (config.productPage) {
    return showAllAccordions.value ? config.accordions : config.accordions.slice(0, 5);
  }
  return faqStore.accordionList;
});

const showViewMoreAccordions = computed(() => {
  return config.productPage && config.accordions.length > 5;
});

const viewMoreAccordionsLabel = computed(() =>
  showAllAccordions.value ? config.viewLessFaqsLabel : config.viewMoreFaqsLabel,
);

const cmsMessageProps = computed(() => {
  return {
    searchTerm: faqStore.selectedCategoryTitle || faqStore.currentSearchTerm || '',
    resultsNumber: faqStore.accordionListLength,
  };
});

const accordionLabel = computed(() =>
  faqStore.resultPage
    ? config.accordionResultsTitle
    : useInterpolatedContent(config.faqsLabel, cmsMessageProps.value),
);
const accordionDescription = computed(() => {
  let description = null;
  if (!faqStore.accordionList.length) {
    description = config.accordionResultsNoFaqsDescription;
  } else if (faqStore.resultPage) {
    description = useInterpolatedContent(config.accordionResultsDescription, cmsMessageProps.value);
  }
  return description;
});

const resultPageClass = computed(() =>
  faqStore.resultPage ? 'c-faq-accordion--result-page' : null,
);

function handleShowAll() {
  showAllAccordions.value = !showAllAccordions.value;

  if (showAllAccordions.value) {
    gaPush({
      event: 'faq_view_more_faqs',
      link_text: config.viewMoreFaqsLabel,
    });
  } else {
    gaPush({
      event: 'faq_view_less_faqs',
      link_text: config.viewLessFaqsLabel,
    });
  }
}

function handleScrollToTop() {
  gaPush({
    event: 'back_to_top',
    link_text: config.accordionMobileScrollLabel,
  });
}

const handleReturnToLanding = () => {
  faqStore.resetLanding();
  gaPush({
    event: 'faq_search_reset',
    link_text: config.searchResultsReturnLabel,
  });
};

function handleAccordionButtonClickGa(el) {
  gaPush({
    event: 'faq_accordion_open',
    faq_title: el.question,
  });
}

function handleLinkClickGa(el) {
  gaPush({
    event: 'faq_accordion_link_click',
    faq_title: el.question,
    link_text: el.readMoreLink,
  });
}

onMounted(() => {
  faqStore.faqAccordionHeadingRef = faqAccordionHeadingRef.value;
});
</script>

<template>
  <div class="c-faq-accordion__background-wrapper">
    <div class="c-faq-accordion" :class="resultPageClass">
      <h2 class="c-faq-accordion__heading" ref="faqAccordionHeadingRef" tabindex="-1">
        {{ accordionLabel }}
      </h2>
      <p class="c-faq-accordion__description" v-if="accordionDescription">
        {{ accordionDescription }}
      </p>
      <a
        href="#faq-landing"
        class="c-faq-accordion__return-btn"
        v-if="faqStore.resultPage"
        @click="handleReturnToLanding"
      >
        <span class="c-faq-accordion__return-btn-icon"
          ><svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M20.7393 5L12.0276 13.7942L3.20551 5.05786L1 7.31427L11.9173 18.5385L23 7.42998L20.7393 5Z"
            /></svg
        ></span>
        <span>{{ config.searchResultsReturnLabel }}</span>
      </a>
      <ul class="c-faq-accordion__list" role="list">
        <li
          class="c-faq-accordion__list-item"
          v-for="el in accordions"
          :key="el.id"
          role="listitem"
          itemscope
          itemprop="mainEntity"
          itemtype="https://schema.org/Question"
        >
          <BaseAccordion
            classPrefix="c-faq-accordion__"
            :id="el.id"
            :isOpen="el.featured && config.productPage"
            :focusAndScroll="el.focusAndScroll"
            @clickAccordionGa="handleAccordionButtonClickGa(el)"
          >
            <template v-slot:heading>
              <span class="c-faq-accordion__icon--star" v-if="el.popular && !faqStore.resultPage">
                <svg
                  aria-hidden="true"
                  focusable="false"
                  viewBox="0 0 22 21"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.4838 0.34857L13.832 7.61283H21.4876C21.9767 7.61283 22.1968 8.24872 21.781 8.54231L15.593 13.0427L17.9655 20.307C18.1122 20.7717 17.5741 21.1631 17.1827 20.8695L10.9946 16.3935L4.80655 20.894C4.41517 21.1874 3.85266 20.7962 4.02378 20.3314L6.39628 13.0672L0.208208 8.56677C-0.183172 8.27331 0.012516 7.63729 0.501661 7.63729H8.15726L10.5054 0.34857C10.6521 -0.11619 11.3369 -0.11619 11.4837 0.34857H11.4838Z"
                  />
                </svg>
                <span class="sr-only">{{ config.popularLabel }}:</span>
              </span>
              <span class="c-faq-accordion__question" itemprop="name">{{ el.question }}</span>
            </template>
            <template v-slot:content>
              <div
                class="c-faq-accordion__content-wrapper"
                itemscope
                itemprop="acceptedAnswer"
                itemtype="https://schema.org/Answer"
              >
                <span
                  class="c-faq-accordion__content-image-wrapper"
                  v-if="el.imageUrl && el.featured && config.productPage"
                >
                  <img
                    class="c-faq-accordion__content-image"
                    :src="el.imageUrl"
                    :alt="el.imageAlt"
                    itemprop="image"
                  />
                </span>
                <div>
                  <div
                    class="c-faq-accordion__content-text"
                    v-html="el.shortAnswer"
                    itemprop="text"
                  />
                  <a
                    class="c-faq-accordion__link"
                    v-if="el.readMoreLink"
                    :href="el.readMoreUrl"
                    itemprop="url"
                    @click="handleLinkClickGa(el)"
                    >{{ el.readMoreLink }}</a
                  >
                </div>
              </div>
            </template>
          </BaseAccordion>
        </li>
      </ul>
      <button
        class="c-faq-accordion__view-more-btn"
        v-if="showViewMoreAccordions"
        @click="handleShowAll"
      >
        {{ viewMoreAccordionsLabel }}
      </button>
    </div>
    <div class="c-faq-accordion__scroll-btn-wrapper">
      <a href="#faq-landing" class="c-faq-accordion__scroll-btn" @click="handleScrollToTop">
        <span class="c-faq-accordion__scroll-btn-icon"
          ><svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M20.7393 5L12.0276 13.7942L3.20551 5.05786L1 7.31427L11.9173 18.5385L23 7.42998L20.7393 5Z"
            /></svg
        ></span>
        <span>{{ config.accordionMobileScrollLabel }}</span>
      </a>
    </div>
  </div>
</template>